import React, { useEffect, useRef } from 'react';
import useChatHistory from '../ChatPage/hooks/useChatHistory';
import { isArray } from 'lodash-es';
import { Conversation } from '@/pages/aigc/pages/Agent/ChatPage/types/history';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { sendIWikiMessage } from './utils';
import type { IHandleSubmitPayload } from '../components/QAInput';
import { MemoizedCopilotSortCompChild } from './components/IWikiSingleQA/components';
import { IWikiSingleQA } from './components/IWikiSingleQA';
import { useMount, useSetState, useSize, useThrottleEffect } from 'ahooks';
import { useCancelIwikiRequest, useGetAnswerData } from './hooks';
import './index.scss';
import { buriedPointPage } from '@home/utils/buriedPoint';
const ChatIWiki = () => {
  const { sessionId, msgId } = useSearchParamsAppoint();
  const [iwikiQuery, setIwikiQuery] = useSetState({
    isLastQA: false,
    css: {},
    userName: '',
    id: '',
  });
  const {
    conversations,
    streamingState,
    startNewConversation,
    // userInfo,
    errorTipsMessage,
    cancelRequest,
    setConversations,
  } = useChatHistory();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading } = useGetAnswerData(setConversations);
  // stop
  const { handleStopStreaming } = useCancelIwikiRequest();
  const size = useSize(document.querySelector('.dba-chat-content'));
  const { isLastQA, userName, id: iwikiId } = iwikiQuery || {};
  const isProcessing = ['pending', 'progressing'].includes(streamingState);
  // 使用 useRef 来捕获最新的 streamingState 值
  const streamingStateRef = useRef(streamingState);
  
  // 当 streamingState 变化时更新 ref
  useEffect(() => {
    streamingStateRef.current = streamingState;
  }, [streamingState]);

  // 接收消息
  useEffect(() => {
    // 监听message事件
    window.addEventListener('message', receiveMessageFromIndex, false);
    // 组件卸载时清理事件监听器
    return () => {
      window.removeEventListener('message', receiveMessageFromIndex, false);
    };
  }, []);

  useEffect(() => {
    // 页面渲染，通知父级页面，已就绪
    handleSendIwikiMessage({ type: 'chatLoaded', payload: { loaded: true } });
    console.log('页面渲染: 通知父级页面，准备开始输出chatLoaded已就绪');
  }, [sessionId]);

  // 当流失状态变更、高度变化，防抖200ms，给父级页面发送信息
  useThrottleEffect(
    () => {
      if (streamingState) {
        const mapStateKey = {
          pending: 'init',
          progressing: 'updating',
          complete: 'succeed',
          rejected: 'error',
        };
        handleSendIwikiMessage({
          type: 'sendStatusData',
          payload: {
            status: mapStateKey?.[streamingState],
            contentHeight: size?.height,
          },
        });
      }
      if (msgId && size?.height !== 0) {
        handleSendIwikiMessage({
          type: 'sendStatusData',
          payload: {
            contentHeight: size?.height,
          },
        });
      }
    },
    [streamingState, size?.height, msgId],
    {
      wait: 200,
    },
  );

  useMount(() => {
    buriedPointPage({
      pageId: '700501',
      pageName: 'iwiki',
      extInfo: JSON.stringify({ sessionId, from: 'iwiki' }),
      pagePath: '/agent/iwiki',
      typeId: 'aigc',
    });
  });

  const handleSubmit = ({
    value,
    fileList: singleMessageFileList,
    options,
    from,
    deleteMessageIds,
  }: IHandleSubmitPayload) => {
    let outConversationsList: Conversation[] = [];
    if (from === 'edit' || from === 'refresh') {
      outConversationsList = conversations?.filter(
        (item) => !deleteMessageIds?.includes(item?.messageId),
      );
    }
    startNewConversation(
      value,
      singleMessageFileList,
      {
        ...options,
        onComplete: () => {
          // fetchChatList();
          options?.onComplete?.();
        },
        onError: () => {
          options?.onError?.();
        },
        onBefore: (msgId) => {
          // 提供msgId给到父级页面, 准备开始对话
          handleSendIwikiMessage({ type: 'sendMsgId', payload: { msgId } });
          // if (from === 'refresh') {
          //   sendIWikiMessage?.({
          //     type: 'retrySession',
          //     payload: { msgId },
          //   });
          // }
          console.log('页面渲染: 通知父级页面，准备开始输出sendMsgId已就绪');
        },
      },
      {
        from,
        outConversationsList,
        serverParams: {
          source: 'iwiki',
          use_dashboard_agent: false,
        },
      },
    );
  };

  // 接受数据
  const receiveMessageFromIndex = (params: any) => {
    const { data } = params;
    const { action, userName = '', payload, id } = data || {};
    const { query, isLastQuestion, css, msgId } = payload || {};
    console.log(data, '接收到的数据');
    if (action === 'stopSession' && streamingStateRef.current && streamingStateRef.current !== 'complete') {
      handleStopStreaming({
        cancelRequest,
        user_id: userName,
        messageId: msgId,
      });
    }
    // 开启新对话时，先存储父级传来的id，后续再使用
    if (action === 'sendSession') {
      handleSubmit({
        value: query,
        fileList: [],
        options: {},
        from: 'send',
      });
      setIwikiQuery({ isLastQA: isLastQuestion, userName, id });
    }
    if (action === 'updateIsLastQuestion') {
      setIwikiQuery({ isLastQA: isLastQuestion, userName });
    }
    if (action === 'changeStyles') {
      setIwikiQuery({ css });
    }
  };

  const handleSendIwikiMessage = (
    data: Parameters<typeof sendIWikiMessage>[0],
  ) => {
    sendIWikiMessage({ options: { id: iwikiId }, ...data });
  };
  return (
    <section className="dba-chat-page dba-chat-page-iwiki">
      <div className="dba-chat-scroll">
        <div className="dba-chat-content">
          {isArray(conversations) &&
            conversations?.map((conversation, index) => (
              <IWikiSingleQA
                {...{ ...conversation, conversations }}
                key={index}
                question={conversation.query}
                streamingState={streamingState}
                isLastQA={isLastQA}
                userInfo={{ openid: userName || '' }}
                handleSubmit={handleSubmit}
                errorTipsMessage={errorTipsMessage}
                isProcessing={isProcessing}
                contentSingleQA={(props: any) => (
                  <MemoizedCopilotSortCompChild {...props} />
                )}
                sendIWikiMessage={handleSendIwikiMessage}
              />
            ))}
        </div>
      </div>
    </section>
  );
};

export default ChatIWiki;
